import React, { useState, useEffect } from 'react';

/**
 * DraftSupplierCheckIn
 *
 * Shown at the top of the Current Draft tab, once per cycle, when
 * the Kin's supplier list has changed since the member last saw it.
 *
 * Displays:
 *   - New suppliers (with KC notes and enable/disable)
 *   - Removed suppliers (why they're gone)
 *   - Suppliers the member has hidden (with "show again")
 *
 * After interaction or dismissal, calls POST /draft-checkin/ack
 * so it won't reappear until the KC changes the list again.
 */
export default function DraftSupplierCheckIn({ kinId, onDone }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hidden, setHidden] = useState([]);   // local copy of vendorsHidden
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!kinId) return;
    load();
  }, [kinId]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/kins/${kinId}/draft-checkin`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load check-in');
      const result = await res.json();
      setData(result);
      setHidden(
        (result.allSuppliers || [])
          .filter((s) => s.hidden)
          .map((s) => s.id)
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const saveHidden = async (newHidden) => {
    setHidden(newHidden);
    try {
      const token = localStorage.getItem('token');
      await fetch(`/api/kins/${kinId}/member-preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ vendorsHidden: newHidden }),
      });
    } catch (err) {
      console.error('Failed to save hidden list:', err);
    }
  };

  const toggleHidden = (vendorId) => {
    const next = hidden.includes(vendorId)
      ? hidden.filter((v) => v !== vendorId)
      : [...hidden, vendorId];
    saveHidden(next);
  };

  const acknowledge = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`/api/kins/${kinId}/draft-checkin/ack`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      console.error('Failed to acknowledge:', err);
    }
    setDismissed(true);
    if (onDone) onDone();
  };

  if (loading) return null;
  if (error) return null;
  if (dismissed) return null;
  if (!data) return null;
  if (!data.hasChanges && !data.isFirstCheckin) return null;

  const { newSuppliers, removedSuppliers, allSuppliers, isFirstCheckin } = data;
  const hiddenSuppliers = allSuppliers.filter((s) => hidden.includes(s.id));

  return (
    <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-4 mb-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="font-semibold text-gray-800">
            👋 {isFirstCheckin ? 'Welcome to your Kin draft' : 'Supplier update'}
          </h3>
          <p className="text-sm text-gray-600 mt-0.5">
            {isFirstCheckin
              ? 'Here are the suppliers available to your Kin. Uncheck any you don\u2019t want to see.'
              : 'A few things have changed since your last draft.'}
          </p>
        </div>
        <button
          onClick={acknowledge}
          className="text-sm text-gray-500 hover:text-gray-700 whitespace-nowrap"
        >
          Dismiss
        </button>
      </div>

      {/* New suppliers */}
      {newSuppliers.length > 0 && (
        <div className="mb-3">
          <div className="text-xs font-medium text-green-700 uppercase tracking-wide mb-1">
            🆕 New this cycle
          </div>
          <div className="space-y-2">
            {newSuppliers.map((s) => (
              <div
                key={s.id}
                className="bg-white rounded-lg p-3 border border-green-200"
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium">
                    {s.type === 'vhub' ? '🛒' : s.type === 'hub' ? '🏪' : '🚜'}{' '}
                    {s.name}
                  </span>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                    {s.type}
                  </span>
                </div>
                {s.note && (
                  <p className="text-sm text-gray-700 mt-1 italic">
                    "{s.note}"
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  {s.productCount} product{s.productCount !== 1 ? 's' : ''} available
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Removed suppliers */}
      {removedSuppliers.length > 0 && (
        <div className="mb-3">
          <div className="text-xs font-medium text-amber-700 uppercase tracking-wide mb-1">
            ⚠️ No longer available
          </div>
          <div className="space-y-1">
            {removedSuppliers.map((s) => (
              <div
                key={s.id}
                className="bg-white rounded-lg p-2.5 border border-amber-200 text-sm"
              >
                <span className="font-medium">{s.name}</span>
                {s.note && (
                  <span className="text-gray-600 italic ml-2">— {s.note}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* First check-in: show all suppliers with notes */}
      {isFirstCheckin && (
        <div className="mb-3">
          <div className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
            Your Kin's suppliers
          </div>
          <div className="space-y-2">
            {allSuppliers.map((s) => (
              <label
                key={s.id}
                className="flex items-start gap-3 bg-white rounded-lg p-3 border border-gray-200 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={!hidden.includes(s.id)}
                  onChange={() => toggleHidden(s.id)}
                  className="w-4 h-4 mt-1"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">
                      {s.type === 'vhub' ? '🛒' : s.type === 'hub' ? '🏪' : '🚜'}{' '}
                      {s.name}
                    </span>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                      {s.type}
                    </span>
                  </div>
                  {s.note && (
                    <p className="text-sm text-gray-700 mt-1 italic">"{s.note}"</p>
                  )}
                  <p className="text-xs text-gray-500 mt-0.5">
                    {s.productCount} product{s.productCount !== 1 ? 's' : ''} available
                  </p>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Currently hidden (when not first check-in) */}
      {!isFirstCheckin && hiddenSuppliers.length > 0 && (
        <div className="mb-3">
          <div className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
            🚫 Hidden from your view
          </div>
          <div className="flex flex-wrap gap-2">
            {hiddenSuppliers.map((s) => (
              <button
                key={s.id}
                onClick={() => toggleHidden(s.id)}
                className="text-sm bg-white border border-gray-200 rounded-full px-3 py-1 hover:bg-gray-50"
              >
                {s.name} — show again
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={acknowledge}
          className="text-sm px-4 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          Continue to shopping
        </button>
      </div>
    </div>
  );
}
